import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES } from '@/i18n';

interface LanguageSwitcherProps {
  tone?: 'light' | 'dark';
}

const labels: Record<string, string> = {
  fr: 'FR',
  en: 'EN',
  it: 'IT',
  de: 'DE',
  pt: 'PT',
  es: 'ES',
};

export default function LanguageSwitcher({ tone = 'dark' }: LanguageSwitcherProps) {
  const { i18n, t } = useTranslation();
  const normalize = (value: string | undefined) => {
    const code = String(value ?? 'fr').slice(0, 2).toLowerCase();
    return (SUPPORTED_LANGUAGES as readonly string[]).includes(code) ? code : 'fr';
  };
  const [lang, setLang] = useState(normalize(i18n.language));

  useEffect(() => {
    setLang(normalize(i18n.language));
  }, [i18n.language]);

  const change = (next: string) => {
    setLang(next);
    i18n.changeLanguage(next);
    try {
      window.localStorage.setItem('monexa_lang', next);
      // Marque un choix volontaire du visiteur : la langue du virement ne
      // doit plus l'écraser ensuite.
      window.sessionStorage.setItem('monexa_lang_manual', '1');
      document.documentElement.lang = next;
    } catch {
      /* storage unavailable */
    }
  };

  const base =
    tone === 'light'
      ? 'bg-background-50/15 border-background-50/25'
      : 'bg-background-200 border-background-300';

  return (
    <div
      className={`inline-flex items-center p-1 rounded-full border ${base}`}
      role="group"
      aria-label={t('nav.language')}
    >
      {SUPPORTED_LANGUAGES.map((code) => {
        const active = lang === code;
        const activeClass =
          tone === 'light'
            ? 'bg-background-50 text-primary-900'
            : 'bg-background-50 text-foreground-950 shadow-sm';
        const idleClass =
          tone === 'light'
            ? 'text-background-100/80 hover:text-background-50'
            : 'text-foreground-500 hover:text-foreground-800';
        return (
          <button
            key={code}
            type="button"
            onClick={() => change(code)}
            aria-pressed={active}
            className={`h-7 min-w-[32px] px-1.5 rounded-full text-[11px] font-bold font-label tracking-wide transition-colors duration-200 cursor-pointer ${
              active ? activeClass : idleClass
            }`}
          >
            {labels[code] ?? code.toUpperCase()}
          </button>
        );
      })}
    </div>
  );
}